import React, {memo, ReactElement, useEffect, useMemo, useRef, useState} from 'react'
import styles from './UIGameLog.module.scss'
import clsx from 'clsx'
import {GameLogData} from '@colonist/game-states'
import {createFeedMessageDataForGameLogData} from '../UIGameFeedMessage/FeedMessageDataLogs'
import {UIGameFeedMessage} from '../UIGameFeedMessage/UIGameFeedMessage'
import {GameValidator, PlayerValidator} from '@colonist/game-mechanic-validators'
import {cloneDeep} from 'lodash-es'
import {UIGameFeedVirtualScrollContainer} from '../UIGameFeedVirtualScrollContainer/UIGameFeedVirtualScrollContainer'
import {usePlayerValidators} from '../../../store/selectors/usePlayerValidators'
import {useGameStore} from '../../../store/GameStoreContext'
import {useIsReplay} from '../../../store/selectors/useIsReplay'

interface UIGameLogFeedMessageProps {
    playerValidator?: PlayerValidator | undefined
    gameValidator: GameValidator
    gameLogData: GameLogData
    opacity: number
    onClick?: ((logIndex: number) => void) | undefined
    logIndex: number
}

const UIGameLogFeedMessage = memo(function UIGameLogFeedMessage({
    playerValidator,
    gameValidator,
    gameLogData,
    opacity = 1,
    onClick,
    logIndex,
}: UIGameLogFeedMessageProps) {
    const translationKeyContext = useGameStore((state) => state.gameMode.translationKeyContext)
    const feedMessageData = createFeedMessageDataForGameLogData(gameValidator, gameLogData, translationKeyContext, playerValidator)

    return (
        <UIGameFeedMessage
            message={feedMessageData}
            onClick={onClick}
            opacity={opacity}
            logIndex={logIndex}
        />
    )
}, (prev, next) => prev.opacity === next.opacity)

// TODO remove once UIReplayController is converted to ReactJS
let globalAddNewLog: undefined | ((logIndex: number, gameLogData: GameLogData) => void)
export function addNewLog(logIndex: number, gameLogData: GameLogData) {
    if(globalAddNewLog) globalAddNewLog(logIndex, gameLogData)
}

export type UIGameLogBackgroundColor = 'blue' | 'beige'

export function UIGameLog({
    isLogVisible,
    onClickLog,
    gameValidator,
    backgroundColor,
}: {
    isLogVisible: boolean
    onClickLog?: (logIndex: number) => void
    gameValidator: GameValidator
    backgroundColor: UIGameLogBackgroundColor
}) {
    const gameLogState = useGameStore((state) => state.gameState.gameLogState)
    const isReplay = useIsReplay()
    const currentPlayerPerspective = useGameStore((state) => state.gameReplay.replayControls?.currentPlayerPerspective)
    const focusLogIndex = useGameStore((state) => state.gameReplay.focusedLogIndex)
    const {getPlayerValidator} = usePlayerValidators()

    const [logs, setLogs] = useState<Record<string, {
        data: GameLogData
        playerValidator?: PlayerValidator | undefined
    }>>({})

    globalAddNewLog = (logIndex, gameLogData) => {
        const newLogData: {
            data: GameLogData
            playerValidator?: PlayerValidator | undefined
        } = {
            data: gameLogData,
        }
        if(gameLogData.from != null) {
            const playerValidator = getPlayerValidator(gameLogData.from)
            // User's avatar can be bot avatar or their actual avatar. cloneDeep to keep the avatar at the state of that particular log
            newLogData.playerValidator = cloneDeep(playerValidator)
        }
        setLogs((prevLogs) => ({
            ...prevLogs,
            [logIndex]: newLogData,
        }))
    }

    // Initial logs from gameState when UIGame is initialized in UIWGameManager
    useEffect(() => {
        if(gameLogState != null) {
            const logsFromState: Record<string, {
                data: GameLogData
                playerValidator?: PlayerValidator | undefined
            }> = {}
            for(const key in gameLogState) {
                const logData = gameLogState[key]
                const logItem: {
                    data: GameLogData
                    playerValidator?: PlayerValidator | undefined
                } = {
                    data: logData,
                }
                if(logData.from != null) {
                    const playerValidator = getPlayerValidator(logData.from)
                    logItem.playerValidator = cloneDeep(playerValidator)
                }
                logsFromState[key] = logItem
            }
            setLogs(logsFromState)
        }
    }, [currentPlayerPerspective])

    const logIndexToChildIndex = useRef(new Map<number, number>())
    const messages = useMemo(() => {
        logIndexToChildIndex.current.clear()
        const msgs: ReactElement[] = []

        for(const key in logs) {
            const logIndex = Number(key)
            const gameLogData = logs[key].data
            const playerValidator = logs[key].playerValidator
            const opacity = focusLogIndex !== null && logIndex > focusLogIndex ? 0.3 : 1
            const message = <UIGameLogFeedMessage
                gameLogData={gameLogData}
                gameValidator={gameValidator}
                key={key}
                logIndex={logIndex}
                onClick={onClickLog}
                opacity={opacity}
                playerValidator={playerValidator}
            />

            logIndexToChildIndex.current.set(logIndex, msgs.length)
            msgs.push(message)
        }

        return msgs
    }, [logs, focusLogIndex, onClickLog])
    const autoScrollToBottom = !isReplay
    const scrollToChildIndex = useMemo(() => {
        if(autoScrollToBottom) return undefined
        if(focusLogIndex === null) return 0

        const childIndex = logIndexToChildIndex.current.get(focusLogIndex)
        if(childIndex === undefined) return 0

        return childIndex
    }, [focusLogIndex, currentPlayerPerspective, autoScrollToBottom])

    if(!isLogVisible) return null

    return (
        <div className={clsx(styles.container, styles[backgroundColor])}>
            <UIGameFeedVirtualScrollContainer autoScrollToBottom={autoScrollToBottom} scrollToChildIndex={scrollToChildIndex}>
                {messages}
            </UIGameFeedVirtualScrollContainer>
        </div>
    )
}
